/**
 * Message with detailed information based on the data  it receives
 *
 * @module views/components/BlockMessage
 * @memberof -Common
 */
import './BlockMessage.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { MessageBlock as PalMessageBlock } from 'web-palette/dist/components/MessageBlock/MessageBlock';

import Button from '@ulta/core/components/Button/Button';
import List from '@ulta/core/components/List/List';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { useToggle } from '@ulta/core/hooks/useToggle/useToggle';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import ActionGroup from '@ulta/modules/ActionGroup/ActionGroup';

import { moveScreenReaderFocus } from '@ulta/utils/accessibility/accessibility';

import BagItemThumbNail from '../BagItemThumbNail/BagItemThumbNail';
import * as utils from './BlockMessage';

/**
 * Represents a BlockMessage component
 *
 * @method
 * @param {BlockMessageProps} props - React properties passed from composition
 * @returns BlockMessage
 */
export const BlockMessage = function( props ){
  const {
    action1,
    action1Style,
    action2,
    action2Style,
    ariaLiveType,
    componentKey,
    description: messageDescription = {},
    dismissAction,
    invokeMutation,
    items,
    loading,
    message,
    messageAction,
    messageType,
    role,
    shouldFocusOnMount,
    showCloseButton = defaultProps.showCloseButton,
    closeCallBack,
    variant = defaultProps.variant
  } = props;

  const { label: description } = messageDescription;
  const formattedMessageType = utils.getMessageType( { messageType } );
  const tabIndex = shouldFocusOnMount ? -1 : undefined;

  const [loader, showLoader, hideLoader] = useLoader( {} );
  const [hideBlock, setHide] = useToggle( false );

  const [visibleProducts, setVisibleProducts] = useState( [] );
  const [closeButtonClicked, setCloseButtonClicked] = useState( false );

  const { breakpoint } = useDeviceInflection();
  const hasMoreItems = items?.length > visibleProducts.length;

  useEffect( () => {
    if( items?.length > 0 ){
      if( breakpoint?.CURRENT_BREAKPOINT === 'SM' ){
        setVisibleProducts( items.slice( 0, 2 ) );
      }
      else {
        setVisibleProducts( items.slice( 0, 4 ) );
      }
    }
  }, [items, breakpoint] );

  useEffect( () => {
    utils.handleHideLoader( { loading, closeButtonClicked }, { hideLoader } );
  }, [loading] );

  const ref = useRef( null );
  useEffect( () => {
    if( shouldFocusOnMount ){
      moveScreenReaderFocus( ref.current );
    }
  }, [] );

  const handleClose = useCallback( () => {
    utils.handleClose(
      { dismissAction },
      { invokeMutation, setCloseButtonClicked, setHide, showLoader, closeCallBack }
    );
  }, [dismissAction, invokeMutation, setHide, showLoader, closeCallBack] );

  if( hideBlock ){
    return null;
  }

  let externalCloseActionElement = null;
  if( showCloseButton ){
    externalCloseActionElement = (
      <Button
        icon
        ariaLabel={ dismissAction?.label }
        iconImage='X'
        iconSize='default'
        variant='unstyled'
        className='BlockMessage__close'
        action={ dismissAction }
        onClick={ handleClose }
      />
    );
  }

  let actionGroups = null;
  if( action1 || action2 ){
    actionGroups = (
      <ActionGroup
        action1={ action1 }
        action2={ action2 }
        action1Style={ action1Style }
        action2Style={ action2Style }
        invokeMutation={ invokeMutation }
      />
    );
  }

  return (
    <div className='BlockMessage'>
      { loader }
      <PalMessageBlock
        { ...spreadProps( {
          ref,
          tabIndex,
          role,
          'aria-live': ariaLiveType,
          description,
          externalCloseActionElement,
          actionGroups,
          messageType: formattedMessageType
        } ) }
        variant={ variant }
        title={ message }
      >
        { messageAction && (
          <Button
            label={ messageAction?.label }
            variant='link'
            iconImage='ArrowForward'
            iconSize='s'
            iconRight={ true }
            action={ { ...messageAction, config: {} } }
            ariaHiddenIcon={ true }
            alignment='right'
            crossButtonVisibility={ true }
            className='BlockMessage__messageAction'
          />
        ) }
        { visibleProducts?.length > 0 && (
          <div className='BlockMessage__ItemThumbnailList'>
            <List display='Inline'
              spacer='00'
            >
              { visibleProducts?.map( ( item, index ) => (
                <div className='BlockMessage__ItemThumbnailSpace'
                  key={ `${componentKey}:${index}:item-tn` }
                >
                  <BagItemThumbNail
                    image={ { ...item.image, width: 48 } }
                    quantity={ item.quantity > 1 ? item.quantity : undefined }
                    thumbSize='md'
                    showBackground={ false }
                  />
                </div>
              ) ) }
            </List>
            { hasMoreItems && (
              <BagItemThumbNail
                thumbSize='sm'
                quantityLabel={ `+${items.length - visibleProducts.length}` }
                showBackground={ false }
              />
            ) }
          </div>
        ) }
      </PalMessageBlock>
    </div>
  );
};

/**
 * DXL messageType mapping based on the messageType
 */
const MESSAGE_TYPE_MAPPING = {
  exclamationtriangle : 'error',
  infocircle : 'info',
  check : 'success'
};

/**
 * Returns messageType based on the messageType or actionIcon
 *
 * @method
 * @param {object} data - message type of BlockMessage
 * @returns getMessageType
 */
export const getMessageType = ( { messageType } ) => {
  const formattedMessageType = MESSAGE_TYPE_MAPPING?.[String( messageType ).toLowerCase()];

  return formattedMessageType || messageType;
};

/**
 * Handles close action
 *
 * @method
 * @param {object} data - required data to be used are passed
 * @param {object} methods - required methods to be used are passed
 */
export const handleClose = ( data, methods ) => {
  const { dismissAction } = data;
  const { invokeMutation, setCloseButtonClicked, setHide, showLoader, closeCallBack } = methods;
  setCloseButtonClicked( true );
  if( dismissAction?.graphql ){
    showLoader();
    invokeMutation( {
      graphql: dismissAction.graphql,
      customHeaders: dismissAction.customHeaders,
      variables: {
        moduleParams: {},
        url: { path: !isServer() && global.location.pathname }
      }
    } );
  }
  else {
    setHide( true );
    closeCallBack && closeCallBack();
  }
};

/**
 * Handles loader hiding
 *
 * @method
 * @param {object} data - required data to be used are passed
 * @param {object} methods - required methods to be used are passed
 */
export const handleHideLoader = ( data, methods ) => {
  const { loading, closeButtonClicked } = data;
  const { hideLoader } = methods;
  closeButtonClicked && !loading && hideLoader();
};

/**
 * Property type definitions
 * @typedef BlockMessageProps
 * @type {object}
 * @property {string} message - content to display
 * @property {string} spacerBetween - margin-bottom in between messages
 * @property {boolean} showCloseButton - for show/hide the close icon
 * @property {string} action1 - Set the action1 for the Message
 * @property {string} action2 - Sets the action2 for the Message
 * @property {string} action1Style - Set the action1Style
 * @property {string} action2Style - Set the action2Style
 * @property {string} description - for showing the list info
 * @property {object} messageAction - sets the link
 * @deprecated {string} icon - Use messageType instead
 * @deprecated {string} backgroundColor - Use messageType and variant instead
 * @deprecated {string} borderColor - Use messageType and variant instead
 * @property {string} image - sets the image url
 * @property {string} quantity - sets the quantity of the products
 * @property {object} dismissAction - sets the close button action
 * @property {function} invokeMutation - Function to invoke a GraphQL mutation
 * @property {array} items - Array of items to display thumbnails for
 * @property {boolean} loading - Indicates if the component is in loading state
 * @property {string} messageType - Type of message (error, warning, info, success)
 * @property {string} role - ARIA role for accessibility
 * @property {boolean} shouldFocusOnMount - When true, sets user focus after mounting component
 * @property {string} dismissAccessibilityLabel - ariaLabel for the close button
 */
export const propTypes = {
  message: PropTypes.string,
  showCloseButton: PropTypes.bool,
  action1: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  action1Style: PropTypes.string,
  action2: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  action2Style: PropTypes.string,
  description: PropTypes.shape( {
    label: PropTypes.string,
    info: PropTypes.string
  } ),
  icon: PropTypes.string,
  backgroundColor: PropTypes.string,
  borderColor: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape( {
      image: PropTypes.shape( {
        imageUrl: PropTypes.string,
        altText: PropTypes.string
      } ),
      quantity: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] )
    } )
  ),
  messageAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  messageType: PropTypes.oneOf( ['error', 'warning', 'info', 'success'] ),
  role: PropTypes.oneOf( ['alert', 'status'] ),
  dismissAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  dismissAccessibilityLabel: PropTypes.string,
  shouldFocusOnMount: PropTypes.bool,
  variant: PropTypes.string
};

/**
 * Default values for passed properties
 * @type object
 * @property { boolean } [ showCloseButton = true ] - show the close button by default
 */
export const defaultProps =  {
  showCloseButton: true,
  variant: 'default'
};

BlockMessage.propTypes = propTypes;
BlockMessage.defaultProps = defaultProps;

export default BlockMessage;
